import { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Map, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import MedjimurjeMap, { MEDJIMURJE_PLACES } from '@/components/MedjimurjeMap';
import MedjimurjeMapV2 from '@/components/MedjimurjeMapV2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCountyMap() {
  const { authFetch } = useAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [cityArticles, setCityArticles] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`${API}/admin/county-map`);
    if (r.ok) setData(await r.json());
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const toggleWidget = async (enabled) => {
    const r = await authFetch(`${API}/admin/county-map/widget`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled, variant: data?.variant || 'v1' }),
    });
    if (r.ok) { toast.success(enabled ? 'Widget uključen na naslovnici' : 'Widget isključen'); load(); }
  };

  const setMapVariant = async (variant) => {
    const r = await authFetch(`${API}/admin/county-map/widget`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: !!data?.widget_enabled, variant }),
    });
    if (r.ok) { toast.success(variant === 'v2' ? 'Aktivirana nova futuristic karta' : 'Vraćena originalna karta'); load(); }
  };

  const openCity = async (city) => {
    setSelected(city);
    const r = await authFetch(`${API}/admin/county-map/articles?city=${encodeURIComponent(city)}&limit=50`);
    if (r.ok) setCityArticles((await r.json()).articles || []);
  };

  if (loading || !data) return <div className="p-10"><Loader2 className="animate-spin text-zinc-500" /></div>;

  const max = Math.max(1, ...(data.items || []).map(i => i.articles));

  return (
    <div className="p-4 md:p-6" data-testid="admin-county-map">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Map className="text-red-500" size={24} />
          <div>
            <h1 className="text-2xl font-bold">Karta Međimurja</h1>
            <p className="text-sm text-zinc-400">Članci grupirani po međimurskim gradovima/općinama.</p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer p-2 bg-zinc-900 border border-zinc-800 rounded">
          <input type="checkbox" checked={!!data.widget_enabled} onChange={e => toggleWidget(e.target.checked)}
                 data-testid="cm-widget-toggle" className="accent-red-600" />
          <span>Prikaži widget na naslovnici</span>
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Ukupno članaka u karti" value={data.total} accent="blue" />
        <Stat label="Prepoznata mjesta" value={(data.items || []).length} accent="violet" />
        <Stat label="Bez lokacije" value={data.unmatched} accent="amber" />
        <Stat label="Sva mjesta (karta)" value={MEDJIMURJE_PLACES.length} accent="cyan" />
      </div>

      {/* Variant toggle (v1 = original, v2 = futuristic) */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-3" data-testid="cm-variant-row">
        <span className="text-xs uppercase tracking-wider text-zinc-400">Stil karte:</span>
        <button onClick={() => setMapVariant('v1')}
                className={`px-3 py-1.5 text-xs font-bold rounded border transition-colors ${
                  (data.variant || 'v1') === 'v1'
                    ? 'bg-red-600 border-red-500 text-white'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-red-500'
                }`}
                data-testid="cm-variant-v1">
          v1 — Klasična (originalna)
        </button>
        <button onClick={() => setMapVariant('v2')}
                className={`px-3 py-1.5 text-xs font-bold rounded border transition-colors ${
                  data.variant === 'v2'
                    ? 'bg-red-600 border-red-500 text-white'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-red-500'
                }`}
                data-testid="cm-variant-v2">
          v2 — Futuristic (nova)
        </button>
        <span className="text-[11px] text-zinc-500 ml-auto">Aktivni stil: <strong className="text-zinc-200">{data.variant || 'v1'}</strong></span>
      </div>

      {/* Interactive SVG preview of the public widget */}
      <div className="mb-6 rounded-lg border border-zinc-800 bg-zinc-950 p-4" data-testid="admin-cm-svg">
        <h3 className="text-xs uppercase tracking-wider text-zinc-400 mb-3">Pregled karte (kako vide posjetitelji)</h3>
        {(data.variant || 'v1') === 'v2' ? (
          <MedjimurjeMapV2
            placeCounts={(data.items || []).reduce((m, i) => { m[i.county] = i.articles; return m; }, {})}
            activePlace={selected}
            onSelect={openCity}
            onSelectRegion={() => {}}
          />
        ) : (
          <MedjimurjeMap
            placeCounts={(data.items || []).reduce((m, i) => { m[i.county] = i.articles; return m; }, {})}
            activePlace={selected}
            onSelect={openCity}
            onSelectRegion={() => {}}
          />
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-zinc-800">
          <h3 className="px-3 py-2 text-xs uppercase tracking-wider text-zinc-400 border-b border-zinc-800">Gradovi</h3>
          <ul className="divide-y divide-zinc-800 max-h-[480px] overflow-y-auto">
            {(data.items || []).map(i => (
              <li key={i.county} className={`px-4 py-2 text-sm cursor-pointer hover:bg-zinc-900 ${selected === i.county ? 'bg-zinc-900' : ''}`}
                  onClick={() => openCity(i.county)} data-testid={`city-${i.county}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold">{i.county}</span>
                  <span className="text-red-400">{i.articles}</span>
                </div>
                <div className="h-1.5 bg-zinc-800 rounded">
                  <div className="h-1.5 bg-red-600 rounded" style={{ width: `${(i.articles / max) * 100}%` }} />
                </div>
              </li>
            ))}
            {(data.items || []).length === 0 && <li className="px-4 py-8 text-center text-zinc-500">Nema podataka.</li>}
          </ul>
        </div>

        <div className="rounded-lg border border-zinc-800">
          <h3 className="px-3 py-2 text-xs uppercase tracking-wider text-zinc-400 border-b border-zinc-800">
            {selected ? `Članci — ${selected}` : 'Odaberi grad'}
          </h3>
          <ul className="divide-y divide-zinc-800 max-h-[480px] overflow-y-auto">
            {!selected && <li className="px-4 py-8 text-center text-zinc-500">Klikni grad lijevo za listu članaka.</li>}
            {cityArticles.map(a => (
              <li key={a.id} className="px-4 py-2 text-sm">
                <a href={`/clanak/${a.slug}`} target="_blank" rel="noreferrer" className="text-zinc-200 hover:text-white font-medium line-clamp-2">{a.title_hr}</a>
                <p className="text-[11px] text-zinc-500 mt-0.5">{(a.publish_at || '').slice(0, 10)}</p>
              </li>
            ))}
            {selected && cityArticles.length === 0 && <li className="px-4 py-4 text-center text-zinc-500 text-sm">Nema članaka za grad.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = 'blue' }) {
  const col = { blue: 'text-blue-400', violet: 'text-violet-400', amber: 'text-amber-400', cyan: 'text-cyan-400' };
  return (
    <div className="p-3 rounded-lg bg-zinc-900 border border-zinc-800">
      <p className="text-[10px] uppercase text-zinc-500">{label}</p>
      <p className={`text-2xl font-bold ${col[accent]}`}>{value}</p>
    </div>
  );
}
